import {
  ArrowsUpDownIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  TagIcon
} from '@heroicons/react/24/outline'
import { PlusIcon } from '@heroicons/react/24/solid'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ActivityIndicator } from '..'
import {
  useNotificationContext,
  usePartnerContext,
  usePaymentContext,
  useUserContext
} from '../../context'
import { MODES } from '../../features/pricing/entitlements'
import { useLimitGuards } from '../../features/pricing/guards'
import { useEntitlements, useLimit } from '../../features/pricing/hooks'
import { classNames, notifyBugsnag } from '../../helpers'
import { ROLES, getAuthorizationRole } from '../../helpers/authorization-roles'
import { usePaymentRestrictions } from '../../hooks/use-payment-restrictions'
import { useTeamMembersQuery } from '../../hooks/use-team-members-query'
import UsageBanner from '../pricing/usage-banner'
import SEO from '../seo'
import ModalCreateUser from './user-management/modal-create-user'
import Users from './user-management/users'

const SORT_OPTIONS = [
  'lastname_asc',
  'lastname_desc',
  'firstname_asc',
  'role',
  'recent'
]

const ROLE_ORDER = ['ADMIN', 'MANAGER', 'WORKER', 'VIEWER']

const SEAT_SOURCE = 'settings_user_management'

/**
 * Zähler im Button „Teammitglied hinzufügen". In enforced gilt das Limit des
 * Plan-Payments, unbekannt oder unbegrenzt zeigt nur die Anzahl. Sonst wie
 * bisher seat_count aus dem PaymentContext, ohne Wert (custom) nur die Anzahl.
 */
export const seatCounterText = ({ count, enforced, limit, seatCount }) => {
  if (enforced) {
    return Number.isFinite(limit) ? `(${count}/${limit})` : `(${count})`
  }

  return seatCount === undefined || seatCount === null
    ? `(${count})`
    : `(${count}/${seatCount})`
}

const getName = ({ firstname, lastname }) => {
  if (!firstname && !lastname) return ''
  if (!firstname) return lastname
  if (!lastname) return firstname

  return `${firstname} ${lastname}`
}

const toMember = ({ space, activeSpace }) => {
  const {
    id: space_id,
    authorization_roles,
    person,
    last_seen,
    owner,
    partner_id,
    tags
  } = space

  if (!person) {
    const message = `/settings/user: person is null - space_id: ${space_id}`
    notifyBugsnag(new Error(message))

    return null
  }

  const firstname = person.firstname || person.invitation_firstname
  const lastname = person.lastname || person.invitation_lastname

  const role = getAuthorizationRole({
    user: {
      username: owner,
      groups: (authorization_roles || []).map((r) => `${r}_${partner_id}`),
      partner_id
    }
  })

  return {
    space_id,
    person,
    firstname,
    lastname,
    name: getName({ firstname, lastname }),
    email: person.email || person.invitation_email,
    role_id: role.id,
    role_title: role.title,
    tags: tags || [],
    last_seen,
    is_current_user: space_id === activeSpace.id
  }
}

const UserManagement = () => {
  const { t } = useTranslation()
  const { partner } = usePartnerContext()
  const { isAdmin: is_aivy_admin, activeSpace } = useUserContext()
  const { seat_count } = usePaymentContext()
  const { error } = useNotificationContext()
  const { mode } = useEntitlements()
  const enforced = mode === MODES.ENFORCED
  const seatLimit = useLimit('seats')
  const { guardSeatInvite } = useLimitGuards()
  const [isCheckingSeats, setIsCheckingSeats] = useState(false)

  const {
    data: users,
    refetch: refetchUsers,
    status
  } = useTeamMembersQuery({ partnerId: partner.id })

  const [openCreateUser, setOpenCreateUser] = useState(false)
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState('all')
  const [tagFilter, setTagFilter] = useState('all')
  const [sortBy, setSortBy] = useState('lastname_asc')

  const restricted = usePaymentRestrictions({
    key: 'seat_count',
    value: users?.length || 0
  })

  const members = useMemo(
    () =>
      (users || [])
        .map((space) => toMember({ space, activeSpace }))
        .filter((member) => member),
    [users, activeSpace]
  )

  const allTags = useMemo(
    () =>
      Array.from(new Set(members.flatMap(({ tags }) => tags))).sort((a, b) =>
        a.localeCompare(b, 'de')
      ),
    [members]
  )

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()

    const list = members.filter((member) => {
      if (roleFilter !== 'all' && member.role_id !== roleFilter) return false
      if (tagFilter !== 'all' && !member.tags.includes(tagFilter)) return false

      if (q) {
        const haystack = [member.name, member.email, ...member.tags]
          .join(' ')
          .toLowerCase()

        if (!haystack.includes(q)) return false
      }

      return true
    })

    const byLastname = (a, b) =>
      (a.lastname || '').localeCompare(b.lastname || '', 'de', {
        sensitivity: 'base'
      })
    const byFirstname = (a, b) =>
      (a.firstname || '').localeCompare(b.firstname || '', 'de', {
        sensitivity: 'base'
      })
    const byRole = (a, b) =>
      ROLE_ORDER.indexOf(a.role_id) - ROLE_ORDER.indexOf(b.role_id) ||
      byLastname(a, b)
    const byRecent = (a, b) => {
      if (a.is_current_user !== b.is_current_user) {
        return a.is_current_user ? -1 : 1
      }

      return new Date(b.last_seen || 0) - new Date(a.last_seen || 0)
    }

    const compare = {
      lastname_asc: byLastname,
      lastname_desc: (a, b) => byLastname(b, a),
      firstname_asc: byFirstname,
      role: byRole,
      recent: byRecent
    }[sortBy]

    return [...list].sort(compare)
  }, [members, search, roleFilter, tagFilter, sortBy])

  const has_admin_role = activeSpace.authorization_role.id === ROLES.ADMIN.id
  const can_invite_teammember = has_admin_role || is_aivy_admin

  if (status === 'pending') return <ActivityIndicator />

  const roleCounts = members.reduce((acc, { role_id }) => {
    acc[role_id] = (acc[role_id] || 0) + 1
    return acc
  }, {})

  const hasActiveFilter =
    roleFilter !== 'all' || tagFilter !== 'all' || search.trim().length > 0

  const resultText = hasActiveFilter
    ? t('settings.user_management.members_filtered_count', {
        filtered: filtered.length,
        total: members.length
      })
    : t('settings.user_management.members_count', { count: members.length })

  const handleOnClickInviteTeammember = async () => {
    // In enforced zählt der Guard frisch und öffnet bei vollem Kontingent den
    // Limit-Dialog, noch bevor das Modal aufgeht. Legacy und preview bleiben
    // beim bisherigen Toast.
    if (enforced) {
      if (isCheckingSeats) return
      setIsCheckingSeats(true)
      try {
        const allowed = await guardSeatInvite({ source: SEAT_SOURCE })
        if (allowed) setOpenCreateUser(true)
      } finally {
        setIsCheckingSeats(false)
      }
      return
    }

    if (restricted) {
      error(t('settings.user_management.max_users_reached_error'))
      return
    }

    setOpenCreateUser(true)
  }

  const rolePills = [
    { id: 'all', label: t('settings.user_management.filter_all'), count: members.length }, // prettier-ignore
    ...ROLE_ORDER.filter((role_id) => roleCounts[role_id]).map((role_id) => ({
      id: role_id,
      label: t(ROLES[role_id].title),
      count: roleCounts[role_id]
    }))
  ]

  return (
    <>
      <div className='max-w-4xl'>
        <SEO title={t('seo.titles.settings_user')} />
        <h1 className='text-xl font-semibold text-gray-900'>
          {t('settings.user_management.settings_container_title')}
        </h1>
        <p className='mt-1 max-w-lg text-sm text-gray-700 lg:max-w-2xl'>
          {t('settings.user_management.settings_container_description')}
        </p>
        {!has_admin_role && (
          <p className='xlg:max-w-2xl mt-4 max-w-lg text-sm italic text-gray-700'>
            {t('settings.user_management.not_admin_hint', {
              role: t(activeSpace.authorization_role.title)
            })}
          </p>
        )}
        {/* Nur in enforced und ab 80 %, rendert sonst nichts */}
        {can_invite_teammember && (
          <UsageBanner resource='seats' source={SEAT_SOURCE} className='mt-4' />
        )}

        <div className='mt-6 flex flex-col gap-3 sm:flex-row'>
          <div className='relative flex-grow'>
            <MagnifyingGlassIcon
              className='pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-gray-400'
              aria-hidden='true'
            />
            <input
              type='text'
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('settings.user_management.search_placeholder')}
              className='block w-full rounded-lg border-gray-300 pl-10 text-sm shadow-sm focus:border-sky-500 focus:ring-sky-500'
            />
          </div>
          {can_invite_teammember && (
            <button
              type='button'
              onClick={handleOnClickInviteTeammember}
              aria-busy={isCheckingSeats || undefined}
              className='inline-flex items-center justify-center whitespace-nowrap rounded-lg bg-primaryBlue px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-darkBlue focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 aria-busy:cursor-wait aria-busy:opacity-80'
            >
              <PlusIcon className='-ml-0.5 mr-1.5 h-5 w-5' aria-hidden='true' />
              {t('settings.user_management.add_user_action')}
              <span className='ml-1.5 font-normal opacity-80'>
                {seatCounterText({
                  count: members.length,
                  enforced,
                  limit: seatLimit.limit,
                  seatCount: seat_count
                })}
              </span>
            </button>
          )}
        </div>

        <div className='mt-4 flex flex-wrap items-center gap-3'>
          <div className='inline-flex gap-0.5 rounded-full bg-gray-100 p-1'>
            {rolePills.map(({ id, label, count }) => (
              <button
                key={id}
                type='button'
                onClick={() => setRoleFilter(id)}
                className={classNames(
                  'inline-flex items-center gap-x-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-[13px] font-semibold transition-all',
                  roleFilter === id
                    ? 'bg-white text-primaryBlue shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                )}
              >
                {label}
                <span className='font-medium opacity-60'>{count}</span>
              </button>
            ))}
          </div>

          {allTags.length > 0 && (
            <div className='relative inline-flex items-center'>
              <TagIcon
                className='pointer-events-none absolute left-3 h-[15px] w-[15px] text-gray-500'
                aria-hidden='true'
              />
              <select
                value={tagFilter}
                onChange={(event) => setTagFilter(event.target.value)}
                className={classNames(
                  'appearance-none rounded-full border py-1.5 pl-8 pr-8 text-[13px] font-semibold focus:border-sky-500 focus:ring-sky-500',
                  tagFilter !== 'all'
                    ? 'border-lightBlue bg-paleWhiteBlue text-primaryBlue'
                    : 'border-gray-300 bg-white text-gray-900'
                )}
              >
                <option value='all'>
                  {t('settings.user_management.filter_all_tags')}
                </option>
                {allTags.map((tag) => (
                  <option key={tag} value={tag}>
                    {tag}
                  </option>
                ))}
              </select>
              <ChevronDownIcon
                className='pointer-events-none absolute right-3 h-4 w-4 text-gray-500'
                aria-hidden='true'
              />
            </div>
          )}

          <div className='ml-auto inline-flex items-center gap-x-2'>
            <span className='text-[13px] font-medium text-gray-500'>
              {t('settings.user_management.sort_label')}
            </span>
            <div className='relative inline-flex items-center'>
              <select
                value={sortBy}
                onChange={(event) => setSortBy(event.target.value)}
                className='appearance-none rounded-full border border-gray-300 bg-white py-1.5 pl-3.5 pr-8 text-[13px] font-semibold text-gray-900 focus:border-sky-500 focus:ring-sky-500'
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {t(`settings.user_management.sort_${option}`)}
                  </option>
                ))}
              </select>
              <ChevronDownIcon
                className='pointer-events-none absolute right-3 h-4 w-4 text-gray-500'
                aria-hidden='true'
              />
            </div>
          </div>
        </div>

        <div className='mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500'>
          <ArrowsUpDownIcon
            className='h-3.5 w-3.5 text-primaryBlue'
            aria-hidden='true'
          />
          <span>
            {t('settings.user_management.sorted_by')}{' '}
            <span className='font-semibold text-gray-900'>
              {t(`settings.user_management.sort_${sortBy}`)}
            </span>
          </span>
          <span className='text-gray-300'>·</span>
          <span>{resultText}</span>
          {hasActiveFilter && (
            <button
              type='button'
              onClick={() => {
                setSearch('')
                setRoleFilter('all')
                setTagFilter('all')
              }}
              className='font-semibold text-primaryBlue hover:text-brightBlue'
            >
              {t('settings.user_management.reset_filters')}
            </button>
          )}
        </div>

        <div className='mt-3'>
          <Users
            members={filtered}
            groupByRole={sortBy === 'role' && roleFilter === 'all'}
            refetchUsers={refetchUsers}
            allTags={allTags}
          />
        </div>
      </div>
      <ModalCreateUser
        open={openCreateUser}
        setOpen={setOpenCreateUser}
        members={members}
        suggestions={allTags}
        onSuccess={() => refetchUsers()}
      />
    </>
  )
}

export default UserManagement
